import { createSlice, PayloadAction, createAsyncThunk } from "@reduxjs/toolkit";
import { RootState } from "reducers";
import { IGetProjectsListRequestParams } from "../../domain/IGetProjectsListRequestParams";
import { IProject } from "../../domain/IProject";
import { projectService } from "../../services/project.service";
import { IPopoverState } from "Dashboard/scenes/Carnets/domain/IPopoverState";
import { IUpdateProjectRequestParams } from "../../domain/IUpdateProjectRequestParams";
import { getIndexById, getNextSortOrder, ISortParams, sort } from "utils/sortUtils";
import { ICreateProjectRequestParams } from "../../domain/ICreateProjectRequestParams";

type GetProjectsListParams = {
  permission: string;
  data?: IGetProjectsListRequestParams;
};

export const getProjectsList = createAsyncThunk<
  IProject[], GetProjectsListParams
>("Projects/getProjectsList",
  async (args, { rejectWithValue }) => {
    const { permission, data } = args;

    try {
      if (permission === "admin") {
        return await projectService.getProjectsAdminList(data);
      } else if (permission === "basic") {
        return await projectService.getProjectsBasicList(data);
      } else {
        return rejectWithValue("No permission to view projects");
      }
    } catch (error: any) {
      return rejectWithValue(error.message || "Failed to fetch projects");
    }
  }
);

export const deleteProject = createAsyncThunk(
  "Projects/deleteProject",
  async (projectId: number) => {
    try {
      return {
        response: await projectService.deleteProject(projectId),
        deletedProjectId: projectId
      };
    } catch (err) {
      setError(err);
    }
  }
);

interface EditNotePopoverState {
  popover: IPopoverState;
  projectId?: number;
  projectIndex?: number;
  note?: string;
};

interface mainState {
  loading: boolean;
  projects: IProject[];
  sort: ISortParams | undefined;
  permission: string;
  selectedIndex: number;
  indexForDeletion: number;
  newProjectPopoverOpen: IPopoverState;
  newProjectFormData: ICreateProjectRequestParams;
  searchProjectsPopoverOpen: IPopoverState;
  deleteProjectModalOpen: boolean;
  editNotePopoverOpen: EditNotePopoverState;
  error: any;
};

const initialState: mainState = {
  loading: false,
  projects: [],
  sort: undefined,
  permission: "",
  selectedIndex: 0,
  indexForDeletion: -1,
  newProjectPopoverOpen: { isOpen: false },
  newProjectFormData: {
    projectName: "",
    typeId: 0,
    categoryId: 0,
    municipalityId: 0,
    clientPartnerId: 0,
    priceValue: 0,
    contractDate: "",
    deadlineForCompletion: "",
    breakfastCost: undefined,
    lunchCost: undefined,
    dinnerCost: undefined,
    cashBreakfastRate: undefined,
    cashLunchRate: undefined,
    cashDinnerRate: undefined,
  },
  searchProjectsPopoverOpen: { isOpen: false },
  deleteProjectModalOpen: false,
  editNotePopoverOpen: { popover: { isOpen: false }, projectId: -1, projectIndex: -1 },
  error: null,
};

export const mainSlice = createSlice({
  name: "projects-main",
  initialState: initialState,
  reducers: {
    setSelectedIndex: (state, action: PayloadAction<number>) => {
      state.selectedIndex = action.payload;
    },
    setProjectPermission(state, action: PayloadAction<string[]>) {
      if (action.payload.includes("project-admin")) {
        state.permission = "admin";
      } else if (action.payload.includes("project")) {
        state.permission = "basic";
      } else {
        state.permission = "none";
      }
    },
    setError: (state, action: PayloadAction<any>) => {
      state.error = action.payload;
    },
    setNewProjectPopoverOpen: (state, action: PayloadAction<IPopoverState>) => {
      state.newProjectPopoverOpen = action.payload;
    },
    updateField: (
      state,
      action: PayloadAction<{ field: keyof ICreateProjectRequestParams, value: any }>
    ) => {
      const { field, value } = action.payload;
      (state.newProjectFormData[field] as string) = value;
    },
    setSearchProjectsPopoverOpen: (state, action: PayloadAction<IPopoverState>) => {
      state.searchProjectsPopoverOpen = action.payload;
    },
    setProjectField: (state, action: PayloadAction<IUpdateProjectRequestParams>) => {
      const { projectId } = action.payload;
      state.projects = state.projects.map((project) =>
        project.projectId === projectId
          ? { ...project, ...action.payload }
          : project)
    },
    setDeleteProjectModalOpen: (
      state,
      action: PayloadAction<{ visible: boolean; index: number }>
    ) => {
      state.deleteProjectModalOpen = action.payload.visible;
      state.indexForDeletion = action.payload.index;
    },
    setEditNotePopoverOpen: (state, action: PayloadAction<EditNotePopoverState>) => {
      state.editNotePopoverOpen = action.payload;
    },
    setSort: (state, action: PayloadAction<ISortParams>) => {
      const newSortOrder = getNextSortOrder(state.sort, action.payload.key);
      const newSortParams = {
        key: action.payload.key,
        typeOfValue: action.payload.typeOfValue,
        order: newSortOrder
      };
      let selectedProjectId = undefined;
      if (state.projects.length > 0) {
        selectedProjectId = state.projects[state.selectedIndex].projectId;
      }
      state.sort = newSortParams;
      state.projects = sort(state.projects, newSortParams);
      state.selectedIndex = getIndexById(
        state.projects,
        selectedProjectId,
        (project: IProject) => project.projectId);
    },
    addCreatedProject: (
      state,
      action: PayloadAction<IProject>
    ) => {
      state.projects.unshift(action.payload);
    },
    updateProject: (
      state,
      action: PayloadAction<{ index: number; project: IProject }>
    ) => {
      state.projects[action.payload.index] = action.payload.project;
    },
  },
  extraReducers: (builder) => {
    builder.addCase(getProjectsList.pending, (state) => {
      state.loading = true;
    });
    builder.addCase(getProjectsList.fulfilled, (state, action) => {
      state.loading = false;
      if (action.payload) {
        if (state.sort) {
          state.projects = sort(action.payload, state.sort);
        } else {
          state.projects = action.payload;
        }
      } else {
        state.projects = [];
      }
      state.selectedIndex = 0;
    });
    builder.addCase(getProjectsList.rejected, (state, action) => {
      state.loading = false;
      state.error = action.error;
    });
    builder.addCase(deleteProject.fulfilled, (state, action) => {
      if (action.payload) {
        state.projects = state.projects.filter(project =>
          project.projectId !== action.payload?.deletedProjectId
        );
      }
    });
  },
});

export const {
  setError,
  setSelectedIndex,
  setProjectPermission,
  setNewProjectPopoverOpen,
  updateField,
  setSearchProjectsPopoverOpen,
  addCreatedProject,
  setProjectField,
  setDeleteProjectModalOpen,
  setEditNotePopoverOpen,
  setSort,
  updateProject,
} = mainSlice.actions;

export const selectState = (state: RootState) => state.projects.main;
export const selectProject = (state: RootState) =>
  state.projects.main.projects[state.projects.main.selectedIndex];
export const selectProjectForDeletion = (state: RootState) =>
  state.projects.main.projects[state.projects.main.indexForDeletion];
export default mainSlice.reducer;
